import styled, { css } from 'styled-components';

import { Colour, Shadow } from '@xemplo/style-constants';

import { boxShadowMap, heightAndWidthMap } from '../checkbox.constants';
import { CheckboxSize } from '../checkbox.types';

/** Minus symbol for indeterminate state */
export const Rectangle = styled.div`
  width: 16px;
  height: 2px;
  background: ${Colour.Clarity.Background.Surface};

  &[data-size='small'] {
    width: 10px;
  }

  &[data-size='medium'] {
    width: 12px;
  }
`;

export const Label = styled.label`
  display: flex;
  flex-shrink: 0;

  box-sizing: border-box;
  position: relative;
  border-radius: 50px;

  width: 24px;
  height: 24px;

  &[data-size='small'] {
    width: 16px;
    height: 16px;
  }

  &[data-size='medium'] {
    width: 20px;
    height: 20px;
  }
`;

/** Styles for checked state (and indeterminate) */
const checkedStyles = css`
  background: ${Colour.Clarity.Blue.Hard};
  color: ${Colour.Clarity.Background.Surface};
  text-align: center;
  border: 2px solid ${Colour.Clarity.Blue.Hard};
  box-shadow: none;
`;

/** Underlying checkbox element.  */
export const HiddenInput = styled.input.attrs({ type: 'checkbox' })`
  height: 0;
  width: 0;
  opacity: 0;
  z-index: -1;
  /** If disabled, remove cursor effect and change opacity */
  &:disabled + span {
    pointer-events: none;
    opacity: 0.2;
  }
`;

/** Styled span is the visual component of the checkbox */
export const Checkbox = styled.span<{ $size: CheckboxSize }>`
  position: absolute;
  cursor: pointer;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  transition: 0.1s all ease-out;
  border-radius: 4px;
  border: 2px solid ${Colour.Clarity.Grey.Medium};
  display: flex;
  align-items: center;
  justify-content: center;

  /** If error state apply red border and shadow */
  &[data-error='true']:not([data-checked='true']) {
    border: 2px solid ${Colour.Clarity.Red.Hard};
    box-shadow: ${Shadow.Error};
  }

  /** needed for the circular box shadow effect on hover */
  &::before {
    content: '';
    position: absolute;
    opacity: 0;
    width: ${({ $size }) => heightAndWidthMap[$size]};
    height: ${({ $size }) => heightAndWidthMap[$size]};
    border-radius: 50%;
    background: ${Colour.Clarity.Grey.Hard};
    box-shadow: ${({ $size }) =>
      `0 0 0 ${boxShadowMap[$size]} ${Colour.Clarity.Grey.Hard}`};
    transition: 0.1s all ease-out;
  }

  /** Hover effects */
  &&:hover {
    border: 2px solid ${Colour.Clarity.Grey.Hard};

    &::before {
      opacity: 0.1;
    }

    &[data-checked='true'] {
      &::before {
        box-shadow: ${({ $size }) =>
          `0 0 0 ${boxShadowMap[$size]} ${Colour.Clarity.Blue.Hard}`};
      }

      &::after {
        content: '';
        position: absolute;
        width: ${({ $size }) => heightAndWidthMap[$size]};
        height: ${({ $size }) => heightAndWidthMap[$size]};
        background: ${Colour.Clarity.Blue.Hard};
        border-radius: 4px;
        transition: 0.1s all ease-out;
      }

      > svg,
      .indeterminate {
        z-index: 10;
      }
    }
  }

  /** If checked, change background color of the span to blue */
  /** Indeterminate state uses same styled as checked - regardless of checked state */
  &[data-checked='true'] {
    ${checkedStyles};
  }
`;
